import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Package, TrendingUp, Users, Settings, 
  Image as ImageIcon, BarChart3, AlertCircle, CheckCircle, X,
  Palette, Info, Loader2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { venueApi } from '../../lib/api';
import { resolveAssetUrl, flag } from '../../config';
import { toast } from 'sonner';
import VenueHeader from '../../components/venue/VenueHeader';

const VenueDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [venuePartner, setVenuePartner] = useState(null);
  const [location, setLocation] = useState(null);
  const [stats, setStats] = useState({
    currentInventory: 0,
    initialInventory: 0,
    totalScans: 0,
    activeAdvertisers: 0,
    dailyCustomerCount: 0,
    daysRemaining: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Access is enforced by <RequireVenue> in App.js, which sends anyone without a
    // venue session to /venue/login. This page still owns the application
    // pending / rejected screens below.
    if (!authLoading) fetchDashboardData();
  }, [user, authLoading]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      if (!user?.venue_partner_id) {
        toast.error('Venue partner profile not found');
        setLoading(false);
        return;
      }

      const venueResponse = await venueApi.get(
        `/venue/partner/${user.venue_partner_id}`
      );

      const venueData = venueResponse.data;
      setVenuePartner(venueData);

      if (venueData.status === 'pending') {
        setLoading(false);
        return;
      }

      if (venueData.hub_location_id) {
        const locationResponse = await venueApi.get(
          `/locations/${venueData.hub_location_id}`
        );
        
        setLocation(locationResponse.data);
        
        const inventory = locationResponse.data.coaster_inventory || 0;
        const dailyUsage = locationResponse.data.daily_customer_estimate || locationResponse.data.daily_customer_count || 0;
        const daysRemaining = dailyUsage > 0 ? Math.floor(inventory / dailyUsage) : 0;
        
        setStats({
          currentInventory: inventory,
          initialInventory: locationResponse.data.initial_coaster_quantity || 0,
          totalScans: locationResponse.data.scan_count || 0,
          activeAdvertisers: locationResponse.data.active_advertisers || 0,
          dailyCustomerCount: dailyUsage,
          daysRemaining: daysRemaining
        });
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getInventoryPercentage = () => {
    if (stats.initialInventory === 0) return 0;
    return (stats.currentInventory / stats.initialInventory) * 100;
  };

  const getInventoryStatus = () => {
    const percentage = getInventoryPercentage();
    if (percentage > 50) return { color: 'green', text: 'Good Stock' };
    if (percentage > 20) return { color: 'amber', text: 'Low Stock' };
    return { color: 'red', text: 'Reorder Soon' };
  };

  // V5 Loading State - Teal spinner on Cream background
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="text-center">
          <img src="/tappd-logo.png" alt="Tappd" className="h-12 mx-auto mb-6" />
          <Loader2 className="animate-spin mx-auto mb-4" size={48} style={{ color: '#14b8a6' }} />
          <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // V5 Pending State
  if (venuePartner?.status === 'pending') {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
        {/* V5 Venue Header — nav hidden: there is no hub to navigate to yet */}
        <VenueHeader user={user} venuePartner={venuePartner} locationData={location} minimal />

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 pt-24">
          {/* V5 Code Accent */}
          <p className="font-mono text-xs mb-2" style={{ color: '#14b8a6' }}>
            // application_pending
          </p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-8 md:p-12 text-center"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #f59e0b',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div 
              className="w-20 h-20 mx-auto mb-6 flex items-center justify-center"
              style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', borderRadius: '12px' }}
            >
              <AlertCircle size={40} style={{ color: '#f59e0b' }} />
            </div>
            <h2 
              className="font-display text-[32px] text-navy mb-4"
            >
              Application Under Review
            </h2>
            <p className="text-[16px] mb-8" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
              Thank you for applying to become a Tappd Hub Location! Your application is currently being reviewed by our team.
            </p>
            
            <div 
              className="p-6 mb-8 text-left"
              style={{ 
                backgroundColor: 'rgba(20, 184, 166, 0.05)',
                border: '1px solid rgba(20, 184, 166, 0.1)',
                borderRadius: '12px'
              }}
            >
              <h3 className="font-semibold text-[14px] mb-4" style={{ color: '#14b8a6' }}>What happens next?</h3>
              <ul className="space-y-3">
                {[
                  "We'll review your application within 48 hours",
                  "You'll receive an email notification with our decision",
                  "Once approved, you'll get full access to this dashboard",
                  "Your free coasters will be prepared and shipped to you"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    <CheckCircle size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#22c55e' }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
              Questions? Contact us at{' '}
              <a href="mailto:hello@tappd.app" className="font-semibold hover:underline" style={{ color: '#14b8a6' }}>
                hello@tappd.app
              </a>
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // V5 Rejected State
  if (venuePartner?.status === 'rejected') {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
        {/* V5 Venue Header — nav hidden: this partner has no hub */}
        <VenueHeader user={user} venuePartner={venuePartner} locationData={location} minimal />

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 pt-24">
          <p className="font-mono text-xs mb-2" style={{ color: '#14b8a6' }}>
            // application_status
          </p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-8 md:p-12 text-center"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #ef4444',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div 
              className="w-20 h-20 mx-auto mb-6 flex items-center justify-center"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: '12px' }}
            >
              <X size={40} style={{ color: '#ef4444' }} />
            </div>
            <h2 
              className="font-display text-[32px] text-navy mb-4"
            >
              Application Not Approved
            </h2>
            <p className="text-[16px] mb-6" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
              Unfortunately, your venue partner application was not approved at this time.
            </p>
            
            {venuePartner?.admin_message && (
              <div 
                className="p-6 mb-6 text-left"
                style={{ 
                  backgroundColor: 'rgba(30, 27, 75, 0.02)',
                  border: '1px solid rgba(30, 27, 75, 0.05)',
                  borderRadius: '12px'
                }}
              >
                <h3 className="font-semibold text-[14px] mb-2" style={{ color: '#1e1b4b' }}>Message from our team:</h3>
                <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>{venuePartner.admin_message}</p>
              </div>
            )}
            
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mb-6 px-6 py-3 text-[14px] font-semibold transition-all"
              style={{
                color: '#14b8a6',
                border: '1px solid rgba(20, 184, 166, 0.3)',
                borderRadius: '12px',
              }}
            >
              Return to homepage
            </button>

            <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
              Questions? Contact us at{' '}
              <a href="mailto:hello@tappd.app" className="font-semibold hover:underline" style={{ color: '#14b8a6' }}>
                hello@tappd.app
              </a>
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  const inventoryStatus = getInventoryStatus();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
      {/* V5 Venue Header with Mobile Nav */}
      <VenueHeader user={user} venuePartner={venuePartner} locationData={location} />

      {/* Content with padding for fixed header */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 pt-24">
        {/* V5 Page Title */}
        <div className="mb-8">
          <p className="font-mono text-xs mb-2" style={{ color: '#14b8a6' }}>
            // venue_dashboard
          </p>
          <h1 
            className="font-display text-[32px] text-navy mb-1"
          >
            {venuePartner?.venue_name || 'Venue Dashboard'}
          </h1>
          <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
            {venuePartner?.venue_type}
          </p>
        </div>

        {/* V5 Stats Cards */}
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {flag('COASTER_INVENTORY') && (<>
          {/* Coaster Inventory */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-5"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #14b8a6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div 
                className="w-12 h-12 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)', borderRadius: '12px' }}
              >
                <Package size={24} style={{ color: '#14b8a6' }} />
              </div>
              <span 
                className="px-3 py-1 text-[12px] font-semibold"
                style={{ 
                  borderRadius: '9999px',
                  backgroundColor: inventoryStatus.color === 'green' ? 'rgba(34, 197, 94, 0.1)' :
                                   inventoryStatus.color === 'amber' ? 'rgba(245, 158, 11, 0.1)' :
                                   'rgba(239, 68, 68, 0.1)',
                  color: inventoryStatus.color === 'green' ? '#22c55e' :
                         inventoryStatus.color === 'amber' ? '#f59e0b' :
                         '#ef4444'
                }}
              >
                {inventoryStatus.text}
              </span>
            </div>
            <p className="text-[13px] mb-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Coaster Inventory</p>
            <p className="text-[28px] font-bold" style={{ color: '#1e1b4b' }}>
              {stats.currentInventory.toLocaleString()}
            </p>
            <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
              of {stats.initialInventory.toLocaleString()} total
            </p>
            <div 
              className="mt-4 h-2 overflow-hidden"
              style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)', borderRadius: '9999px' }}
            >
              <div 
                className="h-full transition-all"
                style={{ 
                  width: `${getInventoryPercentage()}%`,
                  borderRadius: '9999px',
                  backgroundColor: inventoryStatus.color === 'green' ? '#22c55e' :
                                   inventoryStatus.color === 'amber' ? '#f59e0b' :
                                   '#ef4444'
                }}
              />
            </div>
          </motion.div>

          </>)}
          {/* Total Scans */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-5"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #f97316',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)', borderRadius: '12px' }}
            >
              <BarChart3 size={24} style={{ color: '#f97316' }} />
            </div>
            <p className="text-[13px] mb-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Total QR Scans</p>
            <p className="text-[28px] font-bold" style={{ color: '#1e1b4b' }}>
              {stats.totalScans.toLocaleString()}
            </p>
            <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>All time</p>
          </motion.div>

          {/* Active Advertisers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-5"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #22c55e',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: '12px' }}
            >
              <Users size={24} style={{ color: '#22c55e' }} />
            </div>
            <p className="text-[13px] mb-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Active Advertisers</p>
            <p className="text-[28px] font-bold" style={{ color: '#1e1b4b' }}>
              {stats.activeAdvertisers}
            </p>
            <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>At your location</p>
          </motion.div>

          {flag('COASTER_INVENTORY') && (<>
          {/* Days Remaining */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white p-5"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #8b5cf6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: '12px' }}
            >
              <TrendingUp size={24} style={{ color: '#8b5cf6' }} />
            </div>
            <p className="text-[13px] mb-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Days Remaining</p>
            <p className="text-[28px] font-bold" style={{ color: '#1e1b4b' }}>
              {stats.daysRemaining > 0 ? `~${stats.daysRemaining}` : 'Reorder'}
            </p>
            <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
              {stats.dailyCustomerCount > 0 
                ? `~${stats.dailyCustomerCount} coasters/day` 
                : 'Based on usage'}
            </p>
          </motion.div>
          </>)}
        </div>

        {/* V5 Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Manage Coasters */}
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            onClick={() => navigate('/venue/coasters')}
            className="bg-white p-6 text-left cursor-pointer transition-all hover:-translate-y-1"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #14b8a6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(20, 184, 166, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(30, 27, 75, 0.04)';
            }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)', borderRadius: '12px' }}
            >
              <Package size={24} style={{ color: '#14b8a6' }} />
            </div>
            <h3 
              className="text-[18px] font-semibold mb-2"
              style={{ color: '#1e1b4b' }}
            >
              Manage Coasters
            </h3>
            <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
              View current design, change monthly design, request reorders
            </p>
          </motion.button>

          {flag('VENUE_ANALYTICS') && (<>
          {/* View Analytics */}
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            onClick={() => navigate('/venue/analytics')}
            className="bg-white p-6 text-left cursor-pointer transition-all hover:-translate-y-1"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #f97316',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(249, 115, 22, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(30, 27, 75, 0.04)';
            }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)', borderRadius: '12px' }}
            >
              <BarChart3 size={24} style={{ color: '#f97316' }} />
            </div>
            <h3 
              className="text-[18px] font-semibold mb-2"
              style={{ color: '#1e1b4b' }}
            >
              View Analytics
            </h3>
            <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
              Track QR scans, see trends, discover popular advertisers
            </p>
          </motion.button>

          </>)}
          {/* View Advertisers */}
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            onClick={() => navigate('/venue/advertisers')}
            className="bg-white p-6 text-left cursor-pointer transition-all hover:-translate-y-1"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #22c55e',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(34, 197, 94, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(30, 27, 75, 0.04)';
            }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: '12px' }}
            >
              <Users size={24} style={{ color: '#22c55e' }} />
            </div>
            <h3 
              className="text-[18px] font-semibold mb-2"
              style={{ color: '#1e1b4b' }}
            >
              View Advertisers
            </h3>
            <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
              See all businesses advertising at your location
            </p>
          </motion.button>
        </div>

        {/* V5 Live Coaster Design Section */}
        {location && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-white p-6 mb-8"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #14b8a6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)', borderRadius: '12px' }}
                >
                  <Palette size={24} style={{ color: '#14b8a6' }} />
                </div>
                <h2 
                  className="font-display text-[24px] text-navy"
                >
                  Your Live Coaster
                </h2>
              </div>
              <div className="flex items-center gap-2" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                <Info size={16} />
                <span className="text-[13px]">Contact Admin to update</span>
              </div>
            </div>
            
            {location.live_coaster_front_url ? (
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <CheckCircle size={18} style={{ color: '#22c55e' }} />
                  <span className="text-[14px] font-medium" style={{ color: '#22c55e' }}>
                    Custom coaster active for your venue
                  </span>
                </div>
                <div className="flex flex-wrap gap-6 justify-center">
                  {/* Front Image */}
                  <div className="text-center">
                    <p 
                      className="text-[12px] uppercase tracking-wide mb-3 font-medium"
                      style={{ color: 'rgba(30, 27, 75, 0.5)' }}
                    >
                      Front
                    </p>
                    <div
                      className="w-36 h-36 sm:w-44 sm:h-44 p-3 flex items-center justify-center"
                      style={{
                        backgroundColor: 'rgba(20, 184, 166, 0.05)',
                        border: '2px solid rgba(20, 184, 166, 0.1)',
                        borderRadius: '12px'
                      }}
                    >
                      <img
                        src={resolveAssetUrl(location.live_coaster_front_url)}
                        alt="Coaster Front"
                        className="max-w-full max-h-full object-contain"
                        style={{ borderRadius: '8px' }}
                      />
                    </div>
                  </div>

                  {/* Back Image */}
                  {location.live_coaster_back_url && (
                    <div className="text-center">
                      <p
                        className="text-[12px] uppercase tracking-wide mb-3 font-medium"
                        style={{ color: 'rgba(30, 27, 75, 0.5)' }}
                      >
                        Back
                      </p>
                      <div
                        className="w-36 h-36 sm:w-44 sm:h-44 p-3 flex items-center justify-center"
                        style={{
                          backgroundColor: 'rgba(20, 184, 166, 0.05)',
                          border: '2px solid rgba(20, 184, 166, 0.1)',
                          borderRadius: '12px'
                        }}
                      >
                        <img
                          src={resolveAssetUrl(location.live_coaster_back_url)}
                          alt="Coaster Back"
                          className="max-w-full max-h-full object-contain"
                          style={{ borderRadius: '8px' }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-[13px] text-center mt-6" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                  This design is displayed when customers scan your coasters
                </p>
              </div>
            ) : (
              <div className="text-center py-8">
                <div 
                  className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)', borderRadius: '12px' }}
                >
                  <ImageIcon size={32} style={{ color: 'rgba(30, 27, 75, 0.3)' }} />
                </div>
                <p className="text-[16px] font-medium mb-2" style={{ color: '#1e1b4b' }}>
                  Using System Default Design
                </p>
                <p className="text-[14px] max-w-md mx-auto" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                  Your venue is currently using the system-wide default coaster design. 
                  Contact your Tappd administrator to set up a custom coaster for your location.
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* V5 Location Info Card */}
        {location && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-white p-6"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #14b8a6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
              {location.logo_url && (
                <div
                  className="w-24 h-24 flex-shrink-0 flex items-center justify-center overflow-hidden"
                  style={{
                    backgroundColor: 'rgba(30, 27, 75, 0.02)',
                    borderRadius: '12px'
                  }}
                >
                  <img
                    src={resolveAssetUrl(location.logo_url)}
                    alt={location.name}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
              <div className="flex-1">
                <h2 
                  className="font-display text-[24px] text-navy mb-4"
                >
                  Your Location
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-[12px] uppercase tracking-wide mb-1 font-medium" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                      Address
                    </p>
                    <p className="text-[14px] font-medium" style={{ color: '#1e1b4b' }}>{location.address}</p>
                    <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                      {location.city}, {location.state} {location.zip_code}
                    </p>
                  </div>
                  <div>
                    <p className="text-[12px] uppercase tracking-wide mb-1 font-medium" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                      Contact
                    </p>
                    <p className="text-[14px] font-medium" style={{ color: '#1e1b4b' }}>{location.phone}</p>
                    <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>{location.email}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default VenueDashboard;
